import { useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import type { VerbatimRespondent } from '../../data/verbatimData';
import { VerbatimCard } from './VerbatimCard';
import { InfoButton } from '../common/Tooltip';

type SortKey = 'none-rate' | 'avg-price' | 'date';

function sortRespondents(list: VerbatimRespondent[], key: SortKey): VerbatimRespondent[] {
  const sorted = [...list];
  switch (key) {
    case 'none-rate':
      sorted.sort((a, b) => b.noneCount - a.noneCount);
      break;
    case 'avg-price':
      sorted.sort((a, b) => (b.avgChosenPrice ?? -1) - (a.avgChosenPrice ?? -1));
      break;
    case 'date':
      sorted.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      break;
  }
  return sorted;
}

const PAGE_SIZE = 30;

interface Props {
  respondents: VerbatimRespondent[];
}

export function VerbatimWall({ respondents }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('none-rate');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const sorted = sortRespondents(respondents, sortKey);
  const visible = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < sorted.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">
          Respondent Verbatims
        </h3>
        <InfoButton helpKey="verbatimWall" size={13} />
        <div className="flex items-center gap-2">
          <ArrowUpDown size={12} className="text-muted" />
          <select
            value={sortKey}
            onChange={e => { setSortKey(e.target.value as SortKey); setVisibleCount(PAGE_SIZE); }}
            className="bg-surface border border-border rounded px-2 py-1 text-xs text-primary focus:outline-none"
          >
            <option value="none-rate">Sort by None Rate</option>
            <option value="avg-price">Sort by Avg Price</option>
            <option value="date">Sort by Date</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((r, i) => (
          <VerbatimCard key={i} respondent={r} />
        ))}
      </div>

      {hasMore && (
        <div className="mt-6 text-center">
          <button
            onClick={() => setVisibleCount(v => v + PAGE_SIZE)}
            className="px-6 py-2 text-sm font-medium text-chart-teal border border-chart-teal/30 rounded-lg hover:bg-chart-teal/5 transition-colors"
          >
            Load More ({sorted.length - visibleCount} remaining)
          </button>
        </div>
      )}
    </div>
  );
}
